import "./SelectSearchInput.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { debounce } from "lodash";
import { ReactNode, useState } from "react";
import AsyncSelect from "react-select/async";

import CustomInput from "components/customInputs/CustomInput";

const DropdownIndicator = () => (
  <div style={{ color: "#ddd", height: 24, width: 32 }}>
    <svg>
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  </div>
);

interface SelectSearchInputProps {
  id?: string | number;
  label?: string;
  field: string;
  filter: string;
  mapToOptions: Function;
  apiService: Function;
  serviceOptions?: {};
  onChange: Function;
  component?: any;
  children?: ReactNode;
  action?: Function;
  isMissingInfo?: boolean;
  warning?: boolean;
  disabled?: boolean;
  debounceTime?: number;
  inputLimit?: number;
  required?: boolean;
  requiredFill?: string | number;
  value?: any;
  onClear?: Function;
}

const SelectSearchInput = ({
  children,
  id,
  label,
  mapToOptions,
  filter,
  apiService,
  component,
  serviceOptions = {},
  onChange,
  isMissingInfo,
  warning,
  disabled = false,
  debounceTime,
  inputLimit = -1,
  required = false,
  requiredFill = "",
  value,
  onClear,
}: SelectSearchInputProps) => {
  const [open, setOpen] = useState<boolean>(false);

  // Async Await was preventing the debounce from working properly
  const autocomplete = (input: string, callback: Function) => {
    if (input.length < inputLimit) {
      callback([]);
      return;
    }
    apiService({
      [filter]: input.length ? input : undefined,
      ...serviceOptions,
    }).then((resp: any) => {
      if (resp.results) {
        callback(mapToOptions(resp.results));
      }
    });
  };

  const debouncedAutoComplete = debounceTime
    ? debounce(autocomplete, debounceTime)
    : autocomplete;

  const labelId = id ? `${id}` : label?.replace(/ /g, "");

  return (
    <CustomInput
      label={label}
      labelId={labelId}
      error={isMissingInfo ? "Error" : undefined}
      warning={warning ? "Warning" : undefined}
    >
      <div className="SelectSearchInput">
        <div
          className={classNames("SelectSearchInput__button", {
            SelectSearchInput__missingInfo: isMissingInfo,
            SelectSearchInput__warning: warning,
          })}
          id={`${labelId}-select-search-button`}
          aria-labelledby={labelId}
          onClick={() => !disabled && setOpen((o) => !o)}
          role="button"
        >
          {children}
          {value && onClear ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              aria-label="Clear Value"
            >
              X
            </button>
          ) : (
            <span>
              <FontAwesomeIcon icon="angle-down" />
            </span>
          )}
        </div>
        {required && (
          <div className="SelectSearchInput--required">
            <input type="text" defaultValue={requiredFill} required />
          </div>
        )}
        {open && (
          <div className="SelectSearchInput__select">
            <AsyncSelect
              isDisabled={disabled}
              // Open Automatically
              autoFocus
              // XHR Options (sends a dispatch)
              defaultOptions
              // @ts-ignore
              loadOptions={debouncedAutoComplete}
              // Control itself won't render the value
              controlShouldRenderValue={false}
              backspaceRemovesValue={false}
              isClearable={false}
              // Other Options
              hideSelectedOptions={false}
              // Force the menu open
              menuIsOpen
              onChange={(v) => {
                setOpen(false);
                onChange(v);
              }}
              placeholder="Search..."
              // Remove some superflous chrome
              components={{
                DropdownIndicator,
                IndicatorSeparator: null,
              }}
              // Make additional stuff show up

              formatOptionLabel={component}
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < inputLimit
                  ? `Requires search term longer than ${
                      inputLimit - 1
                    } characters`
                  : "No Options"
              }
              tabSelectsValue={false}
              isMulti={false}
              //value={value}

              theme={(theme) => ({
                ...theme,
                borderRadius: 0,
                colors: {
                  ...theme.colors,
                  primary25: "var(--green15)",
                  primary50: "var(--green25)",
                  primary75: "var(--green50)",
                  primary: "var(--teal)",
                },
              })}
              classNamePrefix="SelectSearchInput__select"
            />
          </div>
        )}
        {open && (
          <div
            className="SelectSearchInput__blanket"
            onClick={() => setOpen(false)}
          ></div>
        )}
      </div>
    </CustomInput>
  );
};

export default SelectSearchInput;
